import { apiRequest, dataBaseUrl } from "./apiRequest";

const LOG_PREFIX = "[accountOfferingsService]";

/**
 * Get account offering data for a specific customer, account, and offering
 * @param {number} customerId - The customer ID
 * @param {string} accountName - The account name
 * @param {number} offeringId - The offering ID
 * @returns {Promise} - Account offering data
 */
const getAccountOffering = async (customerId, accountName, offeringId) => {
  try {
    console.log(`${LOG_PREFIX} getAccountOffering`, {
      customerId,
      accountName,
      offeringId,
    });

    const response = await apiRequest(
      dataBaseUrl,
      `/account-offerings/customer/${customerId}/account/${encodeURIComponent(
        accountName
      )}/offering/${offeringId}`,
      {
        method: "get",
        withAuth: true,
      }
    );

    return response;
  } catch (error) {
    console.error(`${LOG_PREFIX} getAccountOffering error`, {
      customerId,
      accountName,
      offeringId,
      error: error.message,
    });
    throw error;
  }
};

/**
 * Get all account offerings for a specific customer and account
 * @param {number} customerId - The customer ID
 * @param {string} accountName - The account name
 * @returns {Promise} - Array of account offerings
 */
const getOfferingsByAccount = async (customerId, accountName) => {
  try {
    console.log(`${LOG_PREFIX} getOfferingsByAccount`, {
      customerId,
      accountName,
    });

    const response = await apiRequest(
      dataBaseUrl,
      `/account-offerings/customer/${customerId}/account/${encodeURIComponent(
        accountName
      )}`,
      {
        method: "get",
        withAuth: true,
      }
    );

    return response;
  } catch (error) {
    console.error(`${LOG_PREFIX} getOfferingsByAccount error`, {
      customerId,
      accountName,
      error: error.message,
    });
    throw error;
  }
};

/**
 * Get account offering data with fallback to legacy system
 * Tries new relational table first, falls back to legacy JSON if not found
 * @param {number} customerId - The customer ID
 * @param {string} accountName - The account name
 * @param {number} offeringId - The offering ID
 * @returns {Promise} - { source: 'new_table' | 'legacy_json', data: object | null }
 */
const getAccountOfferingData = async (customerId, accountName, offeringId) => {
  try {
    console.log(
      `${LOG_PREFIX} getAccountOfferingData - trying new table first`,
      {
        customerId,
        accountName,
        offeringId,
      }
    );

    // First, try to get data from the new relational table
    try {
      const newTableData = await getAccountOffering(
        customerId,
        accountName,
        offeringId
      );
      if (newTableData && newTableData.id) {
        console.log(
          `${LOG_PREFIX} getAccountOfferingData - found in new table`,
          {
            customerId,
            accountName,
            offeringId,
            hasData: true,
          }
        );

        // Convert new table format to legacy format for compatibility
        const legacyFormatData = {
          type: newTableData.employee_commission_type || "rate",
          value: newTableData.employee_commission_value || 0,
          price: newTableData.price,
          cog: newTableData.cost_of_goods || 0,
          cog_type: newTableData.cost_of_goods_type || "rate",
          col: newTableData.cost_of_labor || 0,
          col_type: newTableData.cost_of_labor_type || "rate",
          cc: newTableData.account_commission_value || 0,
          cc_type: newTableData.account_commission_type || "rate",
          isActive:
            newTableData.is_active !== undefined
              ? newTableData.is_active
              : true,
        };

        return {
          source: "new_table",
          data: legacyFormatData,
        };
      }
    } catch (error) {
      console.log(
        `${LOG_PREFIX} getAccountOfferingData - new table error, falling back to legacy`,
        {
          customerId,
          accountName,
          offeringId,
          error: error.message,
        }
      );
      // Continue to fallback below
    }

    // Fallback to legacy system - return indication that we should use legacy
    console.log(
      `${LOG_PREFIX} getAccountOfferingData - no new table data found, using legacy system`,
      {
        customerId,
        accountName,
        offeringId,
      }
    );

    return {
      source: "legacy_json",
      data: null, // Caller should use existing legacy data
    };
  } catch (error) {
    console.error(`${LOG_PREFIX} getAccountOfferingData - error`, {
      customerId,
      accountName,
      offeringId,
      error: error.message,
    });

    // On any error, fall back to legacy system
    return {
      source: "legacy_json",
      data: null,
    };
  }
};

/**
 * Get all account offerings for a customer with enhanced data
 * This function will merge new table data with legacy offering data where available
 * @param {number} customerId - The customer ID
 * @param {string} accountName - The account name
 * @param {Array} legacyOfferings - The existing offerings from legacy system
 * @returns {Promise} - Enhanced offerings array with pricing data
 */
const getEnhancedOfferingsData = async (
  customerId,
  accountName,
  legacyOfferings
) => {
  try {
    console.log(`${LOG_PREFIX} getEnhancedOfferingsData`, {
      customerId,
      accountName,
      offeringsCount: legacyOfferings.length,
    });

    // Get all account offerings for this customer/account from new table
    let newTableOfferings = [];
    try {
      newTableOfferings = await getOfferingsByAccount(customerId, accountName);
      console.log(
        `${LOG_PREFIX} getEnhancedOfferingsData - found new table data`,
        {
          newTableCount: newTableOfferings.length,
        }
      );
    } catch (error) {
      console.log(
        `${LOG_PREFIX} getEnhancedOfferingsData - no new table data, using legacy only`,
        {
          error: error.message,
        }
      );
      // Continue with legacy data only
    }

    // Create a map of offering ID to new table data for quick lookup
    const newTableMap = {};
    newTableOfferings.forEach((item) => {
      newTableMap[item.offering_id] = item;
    });

    // Enhance legacy offerings with new table data where available
    const enhancedOfferings = legacyOfferings.map((offering) => {
      const newTableData = newTableMap[offering.id];

      if (newTableData) {
        // We have new table data for this offering - use it
        console.log(
          `${LOG_PREFIX} getEnhancedOfferingsData - enhancing offering ${offering.id} with new table data`
        );

        // Create enhanced commission data that overrides legacy data
        const enhancedCommissionData = {
          ...offering.json_data.commissions[accountName],
          type: newTableData.employee_commission_type || "rate",
          value: newTableData.employee_commission_value || 0,
          price: newTableData.price,
          cog: newTableData.cost_of_goods || 0,
          cog_type: newTableData.cost_of_goods_type || "rate",
          col: newTableData.cost_of_labor || 0,
          col_type: newTableData.cost_of_labor_type || "rate",
          cc: newTableData.account_commission_value || 0,
          cc_type: newTableData.account_commission_type || "rate",
          isActive:
            newTableData.is_active !== undefined
              ? newTableData.is_active
              : true,
          _dataSource: "new_table", // Internal flag to track data source
        };

        return {
          ...offering,
          json_data: {
            ...offering.json_data,
            commissions: {
              ...offering.json_data.commissions,
              [accountName]: enhancedCommissionData,
            },
          },
        };
      } else {
        // No new table data - use legacy data as-is
        if (offering.json_data.commissions[accountName]) {
          offering.json_data.commissions[accountName]._dataSource =
            "legacy_json";
        }
        return offering;
      }
    });

    console.log(
      `${LOG_PREFIX} getEnhancedOfferingsData - completed enhancement`,
      {
        enhancedCount: enhancedOfferings.length,
        newTableEnhanced: enhancedOfferings.filter(
          (o) =>
            o.json_data.commissions[accountName]?._dataSource === "new_table"
        ).length,
      }
    );

    return enhancedOfferings;
  } catch (error) {
    console.error(`${LOG_PREFIX} getEnhancedOfferingsData - error`, {
      customerId,
      accountName,
      error: error.message,
    });

    // On error, return legacy offerings as-is
    return legacyOfferings;
  }
};

export default {
  getAccountOffering,
  getOfferingsByAccount,
  getAccountOfferingData,
  getEnhancedOfferingsData,
};
